import React, { useState, useMemo } from "react"
import useFetchFutureHuddle from "../hooks/useFetchFutureHuddle"
import useFetchTemplates from "../hooks/useFetchTemplates"
import useFetchSettings from "../hooks/useFetchSettings"
import useFetchFutureSchedules from "../hooks/useFetchSchedules/useFetchFutureSchedules"
import ExpandedView from "../components/FutureHuddles/ExpandedView"
import FutureHuddleSummarySection from "../components/FutureHuddles/FutureHuddleSummarySection"
import FutureHuddleDayOverview from "../components/FutureHuddles/FutureHuddleDayOverview"
import FutureHuddleSkeleton from "../components/FutureHuddles/FutureHuddleSkeleton"

const FutureHuddleView = ({ sidebarOpen }) => {
	const { futureHuddleData, futureHuddleStatus, futureHuddleError } = useFetchFutureHuddle()
	const { settingsData, settingsStatus } = useFetchSettings()
	const { templatesData } = useFetchTemplates()
	const [selectedDay, setSelectedDay] = useState(null)

	// Extract dates from future huddle data for prioritized batch fetching
	const datesToFetch = useMemo(() => {
		if (!futureHuddleData) return []

		// Sort dates to prioritize the next few days (most important for users)
		const sortedDates = futureHuddleData
			.map(day => day.date)
			.sort((a, b) => new Date(a) - new Date(b))

		return sortedDates
	}, [futureHuddleData])

	// Fetch future schedules for all days using the unified hook
	const { data: futureSchedulesData, isLoading: schedulesLoading } = useFetchFutureSchedules(
		settingsData,
		datesToFetch
	)

	// Calculate loading progress for schedules
	const schedulesProgress = useMemo(() => {
		if (!datesToFetch.length) return 0
		const loadedDates = Object.keys(futureSchedulesData || {}).length
		return Math.round((loadedDates / datesToFetch.length) * 100)
	}, [datesToFetch.length, futureSchedulesData])

	// Progressive loading states - show content as it becomes available
	const isInitialLoading = settingsStatus === "loading" || !settingsData
	const hasFutureHuddleData = futureHuddleData && futureHuddleData.length > 0
	const hasTemplatesData = templatesData && Object.keys(templatesData).length > 0
	const templatesLoading = !hasTemplatesData && futureHuddleStatus !== "idle"

	// Show skeleton while waiting for critical data (settings)
	if (isInitialLoading) {
		return <FutureHuddleSkeleton sidebarOpen={sidebarOpen} />
	}

	// Show error state if there's a critical error
	if (futureHuddleError) {
		return (
			<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
				<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-4 sm:px-6 lg:px-8 print-hidden">
					<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-4 sm:mb-0">
						Future Huddle
					</h1>
				</div>
				<div className="px-4 sm:px-6 lg:px-8">
					<div className="bg-red-50 border border-red-200 rounded-lg p-4">
						<p className="text-red-800">
							Error fetching future huddle data: {futureHuddleError}
						</p>
					</div>
				</div>
			</div>
		)
	}

	const handleBack = () => setSelectedDay(null)

	// If a day is selected, show expanded view
	if (selectedDay) {
		return (
			<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
				<ExpandedView day={selectedDay} onBack={handleBack} settings={settingsData} />
			</div>
		)
	}

	// Otherwise show the summary and overview with progressive loading
	return (
		<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
			<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-4 sm:px-6 lg:px-8 print-hidden">
				<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-4 sm:mb-0">
					Future Huddle
				</h1>
			</div>
			<div className="px-4 sm:px-6 lg:px-8">
				{/* Show summary section with loading state */}
				{hasFutureHuddleData ? (
					<FutureHuddleSummarySection
						futureHuddleData={futureHuddleData}
						settings={settingsData}
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-8">
						{[1, 2, 3].map(i => (
							<div
								key={i}
								className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
							>
								<div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
								<div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
								<div className="h-4 bg-gray-200 rounded w-2/3"></div>
							</div>
						))}
					</div>
				)}

				{/* Show day overview with loading state */}
				{hasFutureHuddleData ? (
					<FutureHuddleDayOverview
						futureHuddleData={futureHuddleData}
						settings={settingsData}
						selectedDay={selectedDay}
						setSelectedDay={setSelectedDay}
						schedulesLoading={schedulesLoading}
					/>
				) : (
					<div className="mt-8">
						<div className="flex items-center justify-between mb-6">
							<div className="flex items-center">
								<div className="w-6 h-6 bg-gray-200 rounded mr-2 animate-pulse"></div>
								<div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
							</div>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{[1, 2, 3, 4, 5, 6].map(i => (
								<div
									key={i}
									className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
								>
									<div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
									<div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
									<div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
									<div className="h-4 bg-gray-200 rounded w-1/2"></div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Show loading indicators */}
				<div className="mt-4 space-y-2">
					{/* Schedules loading with progress */}
					{schedulesLoading && hasFutureHuddleData && (
						<div className="flex items-center justify-center">
							<div className="flex flex-col items-center text-gray-600">
								<div className="flex items-center mb-2">
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
									Loading schedule details... {schedulesProgress}%
								</div>
								<div className="w-64 bg-gray-200 rounded-full h-2">
									<div
										className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
										style={{ width: `${schedulesProgress}%` }}
									></div>
								</div>
							</div>
						</div>
					)}

					{/* Templates loading indicator */}
					{templatesLoading && (
						<div className="flex items-center justify-center">
							<div className="flex items-center text-gray-500 text-sm">
								<div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
								Loading templates...
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default FutureHuddleView
